import {
  Alert,
  Badge,
  Item,
  ItemDescription,
  ItemGroup,
  ItemHeader,
  ItemTitle,
  ScrollArea,
  Sentinel,
  Skeleton,
  Spinner,
  TextWithDescription,
  useInfiniteScroll,
} from '@ibsys/kit';
import { FC, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useInfiniteUsers } from '@/entities/users';
import { IsActiveBadge } from '@/features/IsActiveBadge';
import { cn, getFullName, useAppNavigate } from '@/shared/lib';

export type UsersListProps = {
  showDeleted?: boolean;
  className?: string;
  onSelect?: (userId: number) => void;
};

export const UsersList: FC<UsersListProps> = ({ showDeleted, className, onSelect }) => {
  const { t } = useTranslation('users');
  const { id } = useParams<{ id?: string }>();

  const viewportRef = useRef<HTMLDivElement>(null);
  const navigate = useAppNavigate();

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteUsers({
    limit: 10,
    isActive: showDeleted ? null : true,
  });

  const users = useMemo(() => data?.users ?? [], [data]);
  const { sentinelRef } = useInfiniteScroll({
    onLoadMore: fetchNextPage,
    hasMore: hasNextPage,
    isLoading: isFetchingNextPage,
    threshold: 500,
    root: viewportRef,
  });

  const handleSelect = (userId: number) => {
    if (onSelect) {
      onSelect(userId);
      return;
    }
    navigate(`/users/lists/${userId}`);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-5 h-full">
        <div className="flex flex-col gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-16" />
          ))}
        </div>
      </div>
    );
  }

  if (!users?.length) {
    return <Alert className="text-muted-foreground p-4" title={t('noUsers')} />;
  }

  return (
    <ScrollArea className={cn('pb-2', className)} viewPortProps={{ ref: viewportRef }}>
      <ItemGroup className="flex flex-col gap-4">
        {users.map((user) => {
          const isActive = String(user.id) === id;
          const fullName = getFullName(user.firstName, user.lastName, user.middleName);

          return (
            <Item
              variant={isActive ? 'muted' : 'outline'}
              className={cn(
                'hover:bg-muted hover:text-muted-foreground',
                isActive && 'bg-accent text-accent-foreground',
              )}
              key={user.id}
              onClick={() => handleSelect(user.id)}
            >
              <ItemHeader>
                <ItemTitle>
                  <TextWithDescription
                    text={<span>{fullName}</span>}
                    description={<span className="text-sm">{user.email}</span>}
                  />
                </ItemTitle>
              </ItemHeader>
              <ItemDescription className="flex gap-1 flex-col">
                <Badge className="text-xs font-light" outline>
                  {user.role?.name}
                </Badge>
                <IsActiveBadge isActive={user.isActive} />
              </ItemDescription>
            </Item>
          );
        })}
        <Sentinel ref={sentinelRef}>
          {isFetchingNextPage && (
            <div className="w-full flex justify-center py-6">
              <Spinner className="size-8" />
            </div>
          )}
        </Sentinel>
      </ItemGroup>
    </ScrollArea>
  );
};
